/*
 * Copyright 2026 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 */
package ai

import kotlinx.serialization.Serializable

/**
 * Cross-platform chart spec emitted by the Wiz 3R AI personalizer.
 *
 * The catalog is intentionally small. The LLM (Phase 2) picks from the
 * predefined subtypes; the rules engine fills in the data points. The
 * platform-specific renderer (Recharts on web, KoalaPlot on iOS/Android)
 * draws the actual chart from the data. The LLM never invents numbers.
 *
 * Sealed-class kotlinx.serialization works by emitting a `type` discriminator
 * in the JSON, so wire compatibility is preserved as new subtypes are added.
 */
@Serializable
sealed class ChartSpec {
    abstract val title: String
    abstract val xAxisLabel: String?
    abstract val yAxisLabel: String?
}

/**
 * Monte Carlo fan chart for forward-looking projections (retirement value,
 * decumulation longevity, goal achievement probability, stress tests).
 *
 * Each percentile list MUST have the same length as [years]. The median is
 * required; the bands are optional so the renderer can degrade gracefully if
 * a particular projection only computed a subset.
 */
@Serializable
data class MonteCarloFan(
    override val title: String,
    override val xAxisLabel: String? = null,
    override val yAxisLabel: String? = null,
    val years: List<Int>,
    val median: List<Double>,
    val band25_75: PercentileBand? = null,
    val band10_90: PercentileBand? = null,
    val band5_95: PercentileBand? = null,
    val annotations: List<ChartAnnotation> = emptyList(),
) : ChartSpec()

@Serializable
data class PercentileBand(
    val lower: List<Double>,
    val upper: List<Double>,
)

/**
 * Annotation pin for a specific point on a chart — e.g., "FHSA cap reached"
 * at year 5. Used for the conversational drill-in pattern.
 */
@Serializable
data class ChartAnnotation(
    val x: Double,
    val y: Double,
    val label: String,
)

/**
 * Goal progress bar — current vs. target with optional projected completion.
 */
@Serializable
data class GoalProgressBar(
    override val title: String,
    override val xAxisLabel: String? = null,
    override val yAxisLabel: String? = null,
    val current: Double,
    val target: Double,
    val deadlineIso: String? = null,
    val projectedCompletionIso: String? = null,
) : ChartSpec()

/**
 * Tiny inline trend chart used inside Sunday Recap-style cards.
 * Months are indexes (0..N-1); the renderer labels them.
 */
@Serializable
data class CategorySparkline(
    override val title: String,
    override val xAxisLabel: String? = null,
    override val yAxisLabel: String? = null,
    val months: List<String>,
    val values: List<Double>,
    val baselineValue: Double? = null,
) : ChartSpec()

/**
 * Federal + provincial tax bracket overlay with the user's projected income
 * marked. Used for "Reason" explanations of marginal-rate-driven decisions.
 */
@Serializable
data class TaxBracketOverlay(
    override val title: String,
    override val xAxisLabel: String? = null,
    override val yAxisLabel: String? = null,
    val brackets: List<TaxBracket>,
    val currentIncome: Double,
) : ChartSpec()

@Serializable
data class TaxBracket(
    val lowerBound: Double,
    val upperBound: Double?,
    val rate: Double,
    val label: String,
)
